import React, { useEffect, useState } from "react";
import { Button } from "components/buttons";
import { UserAccess } from "components/user-access";
import { ListingTable } from "components/listing";
import { LicenceCheckingService } from "services";
import { AlertConfirm, NotificationToaster } from "components/notifications";
import { LicenceCheckingEditUserModal } from "pages/licence-checking/settings/users/edit";
import { Intent } from "@blueprintjs/core";
import { LicenceCheckingAddUserModal } from "pages/licence-checking/settings/users/add";
import { useSelector } from "react-redux";
import { FriendlyBoolean } from "components/formatting/FriendlyBoolean";

export function LicenceCheckingUsers() {
    const headers = [
        "email address",
        "name",
        "Can Purchase Credits",
        "Can Perform Manual Checks",
        "Can Access Grey Fleet",
        "edit",
        "delete",
    ];
    const listSize = 25;
    const sortedColumns = [{ headerName: "email address", sortName: "emailAddress" }, { headerName: "name", sortName: "firstName" }];
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedDir, setSortedDir] = useState("A");
    const [sortedBy, setSortedBy] = useState(sortedColumns[0]);
    const [isLoading, setIsLoading] = useState(true);
    const [licenceCheckingUsers, setLicenceCheckingUsers] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedUser, setSelectedUser] = useState({});
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
    const loggedInUserId = useSelector(state => state.loggedInUser.userId);

    useEffect(getLicenceCheckingUsers, [sortedBy, sortedDir, pageNumber]);

    useEffect(() => {
        if (!licenceCheckingUsers || licenceCheckingUsers.length === 0) {
            return;
        }

        setTableRows(
            licenceCheckingUsers.map((user) => {
                return [
                    user.emailAddress,
                    `${user.firstName} ${user.lastName}`,
                    <div key={`allow-purchase-credits.${user.userId}`}><FriendlyBoolean>{user.allowPurchaseCredits}</FriendlyBoolean></div>,
                    <div key={`perform-manual-checks.${user.userId}`}><FriendlyBoolean>{user.performManualChecks}</FriendlyBoolean></div>,
                    <div key={`allow-access-grey-fleet.${user.userId}`}><FriendlyBoolean>{user.allowAccessGreyFleet}</FriendlyBoolean></div>,
                    <div key={`${user.Id}-edit`} className="text-align-center">
                        <Button
                            iconOnly
                            icon="edit"
                            minimal
                            onClick={() => showEditModal(user)}
                            large={false}
                        />
                    </div>,
                    <div
                        key={`${user.Id}-delete`}
                        className="text-align-center"
                    >
                        <Button
                            iconOnly
                            icon="trash"
                            minimal
                            intent="Danger"
                            onClick={() => showDeleteModal(user)}
                            large={false}
                        />
                    </div>,
                ];
            })
        );
    }, [licenceCheckingUsers]);

    function getLicenceCheckingUsers() {
        let searchPayload = {
            requestCount: listSize,
            pageNumber: pageNumber,
            searchTerm: "",
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            filters: [],
        };

        setIsLoading(true);

        LicenceCheckingService.getLicenceCheckingUsers(searchPayload)
            .then(
                (results) => {
                    setLicenceCheckingUsers(results.data.filter(user => user.userId !== loggedInUserId));
                    setTotalRecords(results.totalCount);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Error loading users"
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Error loading users");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function onTableSort(header, direction) {

        setSortedBy(header);
        setSortedDir(direction);
    }

    function handleDeleteUserConfirmation() {
        setIsSaving(true);

        if (!selectedUser || !selectedUser.userId) {
            NotificationToaster.show(Intent.DANGER, "Error deleting user");
        }

        LicenceCheckingService.deleteLicenceCheckingUser(selectedUser.userId)
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "User deleted successfully"
                    );
                    hideDeleteModal();
                    getLicenceCheckingUsers();
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Error deleting user"
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Error deleting user");
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    function showDeleteModal(user) {
        setSelectedUser(user);
        setIsDeleteModalVisible(true);
    }

    function hideDeleteModal() {
        setSelectedUser({});
        setIsDeleteModalVisible(false);
    }

    function showEditModal(user) {
        setSelectedUser(user);
        setIsEditModalVisible(true);
    }

    function hideEditModal() {
        setSelectedUser({});
        setIsEditModalVisible(false);
        getLicenceCheckingUsers();
    }

    function showAddUserModal() {
        setIsAddUserModalVisible(true);
    }

    function hideAddUserModal() {
        setIsAddUserModalVisible(false);
    }

    return (
        <UserAccess
            perform={"LicenceChecking:Manage"}
            yes={() => (
                <div className="row">
                    <h2>Users</h2>

                    <div className="spacer-bottom">
                        <Button
                            text="Add User"
                            onClick={showAddUserModal}
                            intent="primary"
                        />
                    </div>

                    <ListingTable
                        id="licence-checking-users"
                        headers={headers}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={isLoading}
                        showPerPage={false}
                        onPagingChange={setPageNumber}
                        sortable
                        sortableHeaders={sortedColumns}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onTableSort}
                    />

                    <AlertConfirm
                        isOpen={isDeleteModalVisible}
                        title="Delete Licence Checking User"
                        onConfirm={handleDeleteUserConfirmation}
                        onCancel={hideDeleteModal}
                        confirmButtonText="Delete"
                        cancelButtonText="Cancel"
                        saving={isSaving}
                    >
                        <p>
                            Are you sure you want to delete{" "}
                            {`${selectedUser.firstName ?? ""} ${
                                selectedUser.lastName ?? ""
                            }`}{" "}
                            as a licence checking user
                        </p>
                    </AlertConfirm>

                    <LicenceCheckingEditUserModal
                        isOpen={isEditModalVisible}
                        onClose={hideEditModal}
                        user={selectedUser}
                    />

                    <LicenceCheckingAddUserModal
                        isOpen={isAddUserModalVisible}
                        hideModal={hideAddUserModal}
                        onSuccess={getLicenceCheckingUsers}
                    />
                </div>
            )}
        />
    );
}
